import React from "react";
import { Link } from "react-router-dom";
import AIGenerator from "../components/AIGenerator";
import LatestDraw from "../components/LatestDraw";
import usePageMeta from "../utils/usePageMeta";
import AdSlot from "../components/AdSlot";

const Home = () => {
  usePageMeta({
    title: null,
    description:
      "California Daily 3 and Daily 4 results as they are published, with digit-frequency analytics explained in plain terms. Independent, informational, and never a prediction.",
    path: "/",
  });

  return (
    <div className="page home">
      <section className="hero">
        <span className="hero-eyebrow">California · Daily 3 &amp; Daily 4</span>
        <h1>Every draw, every digit, honestly explained.</h1>
        <p>
          Official results as they land, the frequency data behind them, and number
          ideas for fun — with the maths spelled out rather than dressed up as a system.
        </p>
        <div className="hero-actions">
          <a className="btn btn-on-brand" href="#results">See today’s results</a>
          <Link className="btn btn-outline" to="/analytics">Explore the numbers</Link>
        </div>
      </section>

      {/* Results first: it is what people arrive for. */}
      <section id="results">
        <h2>Latest results</h2>
        <div className="grid">
          <LatestDraw game="Daily3" />
          <LatestDraw game="Daily4" />
        </div>
      </section>

      {/* Below the results, which is what people came for. */}
      <AdSlot slot={process.env.REACT_APP_ADSENSE_SLOT_HOME} />

      <div className="info-note">
        <span aria-hidden="true">⚠️</span>
        <span>
          <strong>Disclaimer:</strong> numbers generated on this site are for
          entertainment and information only. They are not official results, they do
          not improve your chances of winning, and we are not affiliated with the
          California Lottery. Please play responsibly.
        </span>
      </div>

      <section>
        <h2>Number generators</h2>
        <p>
          A daily pick per game, seeded so it stays the same all day. Treat it as a
          coin toss with better styling — every combination has identical odds.
        </p>
        <div className="grid">
          <AIGenerator game="Daily3" />
          <AIGenerator game="Daily4" />
        </div>
      </section>

      <section className="prose">
        <h2>About Daily 3 &amp; Daily 4</h2>
        <p>
          Daily 3 and Daily 4 ask you to choose three or four digits. Each digit is
          drawn independently from 0–9, so there are 1,000 possible Daily 3
          combinations and 10,000 for Daily 4 — and each is equally likely on every
          draw.
        </p>

        <h3>Do “hot” and “cold” numbers exist?</h3>
        <p>
          Not in a way you can use. The draw has no memory: a digit that has appeared
          five times this week is exactly as likely to appear tonight as one that
          hasn’t appeared at all. Short runs of results always look streaky, which is
          why our{" "}
          <Link to="/analytics">frequency chart</Link> plots the expected value
          alongside the actual counts — the gap between them is noise, not signal.
        </p>

        <h3>How LottoNest works</h3>
        <p>
          We read published results from the California Lottery and compute the
          statistics ourselves. Number ideas come from seeded randomisation, not
          prediction: nothing here can forecast a draw, and any site that claims
          otherwise is selling something.
        </p>
      </section>
    </div>
  );
};

export default Home;
